import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';
import { LogIn, LogOut, ChevronRight, RefreshCw, CheckCircle2, TimerOff } from 'lucide-react';
import api from '../utils/api';

// Clock-in / clock-out alerts for the dashboard: the shifts where a worker forgot to clock out
// (clocked in, shift ended, no clock-out), the ones they never clocked in to, and the ones Rostery
// clocked out automatically (waiting for a manager to check the times). Each row says who, which
// client, which day and what time, and opens EVV, or Timesheets for an automatic clock-out.
// Refreshes every 2 minutes and when the window regains focus.

const t12 = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).slice(0, 5).split(':').map(Number);
  const ap = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, '0')} ${ap}` : `${h12} ${ap}`;
};
const dayLabel = (d) => {
  try {
    const dt = parseISO(d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((today - dt) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return format(dt, 'EEE d MMM');
  } catch { return d; }
};
const ago = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h} h ${m % 60 ? `${m % 60} min` : ''}`.trim();
  return `${Math.floor(h / 24)} days`;
};

const BASIS = { rostered: 'at the rostered finish', gps: 'when GPS showed them leaving', cap: '16 hours after clock-in' };
function AutoRow({ r }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/70">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-violet-50 text-violet-600" aria-hidden="true"><TimerOff size={15} /></span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 truncate">
          <span className="font-semibold">{r.staff_name}</span>
          {r.client_names && <span className="text-slate-500"> · {r.client_names}</span>}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          <span className="font-medium text-slate-700">{r.shift_date ? dayLabel(r.shift_date) : 'Shift'}</span>{r.start_time ? `, ${t12(r.start_time)} – ${t12(r.end_time)}` : ''}
          {' · '}<span className="text-violet-700 font-medium">clocked out {t12(r.clock_out_hm)} {BASIS[r.basis] || ''}</span>
          {r.suggested_out_hm && r.basis !== 'gps' && <> · GPS: left {t12(r.suggested_out_hm)}</>}
        </p>
      </div>
      <Link to="/timesheets" className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary-700 hover:text-primary-900 shrink-0"
        aria-label={`Check ${r.staff_name}'s times`}>
        Check <ChevronRight size={13} />
      </Link>
    </li>
  );
}

function Row({ r, kind }) {
  const out = kind === 'out';
  const clockedIn = r.clock_in_epoch ? format(new Date(r.clock_in_epoch * 1000), 'h:mm a').toLowerCase() : null;
  return (
    <li className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50/70">
      <span className={clsx('w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
        out ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600')} aria-hidden="true">
        {out ? <LogOut size={15} /> : <LogIn size={15} />}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-800 truncate">
          <span className="font-semibold">{r.staff_name}</span>
          {r.client_names && <span className="text-slate-500"> · {r.client_names}</span>}
        </p>
        <p className="text-xs text-slate-500 mt-0.5">
          <span className="font-medium text-slate-700">{dayLabel(r.shift_date)}</span>, {t12(r.start_time)} – {t12(r.end_time)}
          {out
            ? <> · clocked in {clockedIn || '—'} · <span className="text-rose-700 font-medium">no clock-out, shift ended {ago(r.minutes_since_end)} ago</span></>
            : <> · <span className="text-amber-700 font-medium">no clock-in, shift started {ago(r.minutes_since_start)} ago</span></>}
        </p>
      </div>
      {r.staff_phone && (
        <a href={`tel:${String(r.staff_phone).replace(/\s+/g, '')}`} className="hidden sm:inline text-xs font-medium text-primary-700 hover:underline shrink-0">
          Call
        </a>
      )}
      <Link to={`/evv?date=${r.shift_date}`} className="inline-flex items-center gap-0.5 text-xs font-semibold text-primary-700 hover:text-primary-900 shrink-0"
        aria-label={`Fix times for ${r.staff_name} on ${dayLabel(r.shift_date)}`}>
        Fix <ChevronRight size={13} />
      </Link>
    </li>
  );
}

export default function AttendanceAlerts() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('out');
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(() => {
    api.get('/dashboard/attendance')
      .then((r) => { setData(r.data); setError(false); })
      .catch(() => setError(true));
  }, []);

  // Open on the list that has something in it.
  const [picked, setPicked] = useState(false);
  useEffect(() => {
    if (picked || !data) return;
    if (!(data.notClockedOut || []).length) {
      if ((data.missedClockIn || []).length) setTab('in');
      else if ((data.autoClockedOut || []).length) setTab('auto');
    }
    setPicked(true);
  }, [data, picked]);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 120000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  if (!data && !error) return null;
  const outs = data?.notClockedOut || [];
  const ins = data?.missedClockIn || [];
  const autos = data?.autoClockedOut || [];
  const list = tab === 'out' ? outs : tab === 'auto' ? autos : ins;
  const shown = showAll ? list : list.slice(0, 5);

  return (
    <section aria-label="Clock-in and clock-out alerts" className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 flex-wrap">
        <div className="flex items-center gap-2.5">
          <span className={clsx('w-8 h-8 rounded-lg flex items-center justify-center',
            outs.length || ins.length || autos.length ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600')} aria-hidden="true">
            {outs.length || ins.length || autos.length ? <LogOut size={16} /> : <CheckCircle2 size={16} />}
          </span>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Clock-in and clock-out alerts</h2>
            <p className="text-xs text-slate-500">Last 14 days. Fix the times in EVV so timesheets and pay are right.</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <div role="tablist" aria-label="Alert type" className="flex gap-0.5 bg-slate-100 rounded-lg p-0.5">
            {[['out', 'Not clocked out', outs.length, 'bg-rose-600'], ['in', 'Missed clock-in', ins.length, 'bg-amber-500'],
              ...(autos.length || tab === 'auto' ? [['auto', 'Auto clocked out', autos.length, 'bg-violet-600']] : [])].map(([k, label, n, dot]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => { setTab(k); setShowAll(false); }}
                className={clsx('h-7 inline-flex items-center gap-1.5 px-2.5 rounded-md text-xs font-semibold transition-colors',
                  tab === k ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')}>
                {label}
                <span className={clsx('min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center tabular-nums',
                  n ? `${dot} text-white` : 'bg-slate-200 text-slate-600')}>{n}</span>
              </button>
            ))}
          </div>
          <button type="button" onClick={load} aria-label="Refresh alerts" className="w-7 h-7 flex items-center justify-center rounded-md text-slate-500 hover:bg-slate-100">
            <RefreshCw size={13} />
          </button>
        </div>
      </div>
      {error ? (
        <p className="px-4 py-4 text-sm text-slate-600">We couldn't load the clock-in alerts. <button onClick={load} className="font-semibold text-primary-700 hover:underline">Try again</button></p>
      ) : list.length === 0 ? (
        <p className="px-4 py-4 text-sm text-slate-600 flex items-center gap-2">
          <CheckCircle2 size={15} className="text-emerald-600" />
          {tab === 'out' ? 'Everyone who clocked in has clocked out.' : tab === 'auto' ? 'No automatic clock-outs to check.' : 'No missed clock-ins.'}
        </p>
      ) : (
        <>
          <ul className="divide-y divide-slate-100">
            {shown.map((r) => (tab === 'auto' ? <AutoRow key={r.timesheet_id} r={r} /> : <Row key={r.id} r={r} kind={tab} />))}
          </ul>
          {list.length > 5 && (
            <button type="button" onClick={() => setShowAll((s) => !s)}
              className="w-full px-4 py-2 text-xs font-semibold text-primary-700 hover:bg-slate-50 border-t border-slate-100">
              {showAll ? 'Show fewer' : `Show all ${list.length}`}
            </button>
          )}
        </>
      )}
    </section>
  );
}
